import * as Sentry from "@sentry/react";
import type { LogAttributes } from "./sentry-reporter";
import { StepTimeline } from "./step-timeline";

export type DocumentReadStep =
    | "bookmark_paragraphs"
    | "read_lock_wait"
    | "get_file"
    | "read_slices"
    | "close_file"
    | "encode_base64"
    | "await_document_name";

export interface OpenedDocumentFile {
    documentBytes: number;
    sliceCount: number;
}

export interface DocumentReadObserver {
    enterStep(step: DocumentReadStep): void;
    fileOpened(openedFile: OpenedDocumentFile): void;
    sliceRead(): void;
}

export const UNOBSERVED_DOCUMENT_READ: DocumentReadObserver = {
    enterStep() {},
    fileOpened() {},
    sliceRead() {},
};

export const DOCUMENT_READ_SPAN_OP = "addin.document_read";

export const DOCUMENT_READ_STEP_SPAN_OP = "addin.document_read.step";

const DOCUMENT_READ_FINISHED = "finished";

export class DocumentReadProgress implements DocumentReadObserver {
    readonly readSpan: Sentry.Span;
    private readonly steps: StepTimeline<DocumentReadStep>;
    private openedFileAttributes: LogAttributes = {};
    private slicesRead = 0;

    constructor(parentSpan: Sentry.Span | null) {
        this.readSpan = Sentry.startInactiveSpan({ name: DOCUMENT_READ_SPAN_OP, op: DOCUMENT_READ_SPAN_OP, parentSpan });
        this.steps = new StepTimeline<DocumentReadStep>(this.readSpan, DOCUMENT_READ_STEP_SPAN_OP);
    }

    enterStep(step: DocumentReadStep): void {
        this.steps.enter(step);
    }

    fileOpened(openedFile: OpenedDocumentFile): void {
        this.openedFileAttributes = { document_bytes: openedFile.documentBytes, slice_count: openedFile.sliceCount };
        this.readSpan.setAttributes(this.openedFileAttributes);
    }

    sliceRead(): void {
        this.slicesRead += 1;
    }

    finish(): void {
        this.steps.finish();
        this.readSpan.setAttribute("slices_read", this.slicesRead);
        this.readSpan.end();
    }

    describe(): LogAttributes {
        return {
            document_read_step: this.steps.stepInProgress() ?? DOCUMENT_READ_FINISHED,
            slices_read: this.slicesRead,
            ...this.openedFileAttributes,
        };
    }
}
